import { Fragment, useState } from "react";
import { Button, Col, Divider, Empty, Form, Modal, Row, Select, Tooltip, Typography } from "antd";
import { BankOutlined, CloseOutlined } from "@ant-design/icons";

import { IFonteSaldoDeContas, ISolicitacaoAtivacao, IContaSaldoDeContas } from "../../../../../interfaces";
import { ICreateFonte, IGetFontes, IUpdateContasFontes } from "../../interfaces";

import { removeAcento } from "../../../../../../../../utils/RemoveAcentos";
import TabTitleCount from "../../../../components/TabTitleCount";
import { useEditarFontesModal } from "./useEditarFontesModal";
import FonteForm from "../FonteForm";

interface IProps {
    visible: boolean;
    onClose: () => void;
    solicitacao?: ISolicitacaoAtivacao | null;
    selectedContas: IContaSaldoDeContas[];
    onRemoveConta?: (contaId: number) => void;
    onGetFontes?: (data: IGetFontes) => Promise<IFonteSaldoDeContas[] | null>;
    onCreateFonte?: (data: ICreateFonte) => Promise<IFonteSaldoDeContas | null>;
    onUpdateContasFontes?: (data: IUpdateContasFontes) => Promise<IContaSaldoDeContas[] | void>;
    onSuccess: (updatedContas?: IContaSaldoDeContas[]) => void;
}

const BankAvatar = ({
    imageUrl,
    nome,
    isLight,
}: {
    imageUrl?: string | null;
    nome: string;
    isLight: boolean;
}) => {
    const [imgError, setImgError] = useState(false);

    return (
        <div
            style={{
                backgroundColor: "#FFFFFF",
                width: 24,
                height: 24,
                minWidth: 24,
                borderRadius: 24,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${isLight ? "#00000015" : "#6f6d6d95"}`,
            }}
        >
            {imageUrl && !imgError ? (
                <img
                    src={imageUrl}
                    width={24}
                    height={24}
                    style={{ borderRadius: 24, objectFit: "contain" }}
                    alt={nome}
                    onError={() => setImgError(true)}
                />
            ) : (
                <BankOutlined style={{ fontSize: 13, color: "#8c8c8c" }} />
            )}
        </div>
    );
};

export default function EditarFontesModal(props: IProps) {
    const {
        visible,
        solicitacao,
        selectedContas,
        onRemoveConta,
        onCreateFonte,
    } = props;

    const {
        form,
        isFonteFormVisible,
        isLoadingFontes,
        fontes,
        isFonteSelectOpen,
        setIsFonteSelectOpen,
        isSubmitting,
        isLight,
        groupedContasByBanco,
        handleCloseFonteForm,
        handleClickAdicionarFonte,
        handleFonteCreated,
        handleClose,
        handleSubmit,
    } = useEditarFontesModal(props);

    return (
        <Modal
            title="Editar fontes das contas"
            open={visible}
            onCancel={handleClose}
            footer={null}
            destroyOnClose
            width={850}
        >
            <Row gutter={[24, 16]}>
                {/* LISTA DE CONTAS (Coluna Esquerda) */}
                <Col xs={24} md={12}>
                    <div style={{ marginBottom: 12 }}>
                        <TabTitleCount
                            descricao="Contas selecionadas"
                            count={selectedContas.length}
                            badgeBackgroundColor="#2B98FF"
                            badgeTextColor="#FFFFFF"
                        />
                    </div>

                    <div
                        style={{
                            maxHeight: 280,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            paddingRight: 6,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                            scrollbarWidth: 'thin',
                            scrollbarColor: isLight ? '#bfbfbf transparent' : '#555555 transparent',
                        }}
                    >
                        {selectedContas.length === 0 ? (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Nenhuma conta selecionada"
                                style={{ margin: '40px 0' }}
                            />
                        ) : (
                            groupedContasByBanco.map((banco) => (
                                <div
                                    key={banco.id}
                                    style={{
                                        flexShrink: 0,
                                        border: `1px solid ${isLight ? '#0000000f' : '#ffffff1f'}`,
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <div
                                        style={{
                                            flexShrink: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '8px 10px',
                                            backgroundColor: isLight ? '#f0f2f56f' : '#00000036',
                                        }}
                                    >
                                        <BankAvatar
                                            imageUrl={banco.imageUrl}
                                            nome={banco.nome}
                                            isLight={isLight}
                                        />

                                        <Typography.Text strong style={{ fontSize: 13 }}>
                                            {banco.nome}
                                        </Typography.Text>
                                    </div>

                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        {banco.contas.map((conta, index) => (
                                            <Fragment key={conta.id}>
                                                <div
                                                    style={{
                                                        flexShrink: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '6px 10px',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flexWrap: 'wrap',
                                                            columnGap: 12,
                                                        }}
                                                    >
                                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                            Conta: <Typography.Text strong>{conta.numero}</Typography.Text>
                                                        </Typography.Text>

                                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                            Agência: <Typography.Text strong>{conta.agencia || '-'}</Typography.Text>
                                                        </Typography.Text>
                                                    </div>

                                                    {onRemoveConta && (
                                                        <Tooltip title="Desmarcar conta">
                                                            <span
                                                                onClick={() => onRemoveConta(conta.id)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: 20,
                                                                    height: 20,
                                                                    borderRadius: '50%',
                                                                    color: isLight ? '#8c8c8c' : '#a6a6a6',
                                                                    transition: 'all 0.2s ease',
                                                                    backgroundColor: 'transparent',
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.backgroundColor = isLight ? '#0000000f' : '#ffffff18';
                                                                    e.currentTarget.style.color = isLight ? '#262626' : '#ffffff';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                                    e.currentTarget.style.color = isLight ? '#8c8c8c' : '#a6a6a6';
                                                                }}
                                                            >
                                                                <CloseOutlined style={{ fontSize: 10 }} />
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </div>

                                                {index < (banco.contas.length - 1) && (
                                                    <Divider style={{ margin: 0 }} />
                                                )}
                                            </Fragment>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </Col>

                {/* SELECT DE FONTES & AÇÕES (Coluna Direita) */}
                <Col xs={24} md={12}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                        <div>
                            <Form layout="vertical" form={form}>
                                <Form.Item
                                    name="fontes"
                                    label="Novas fontes para as contas selecionadas"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Informe pelo menos 1 fonte de recurso para as contas.',
                                            type: 'array',
                                            min: 1,
                                        },
                                    ]}
                                >
                                    <Select
                                        open={isFonteSelectOpen}
                                        onDropdownVisibleChange={setIsFonteSelectOpen}
                                        showSearch
                                        allowClear
                                        loading={isLoadingFontes}
                                        mode="multiple"
                                        placeholder="Selecione as fontes..."
                                        optionLabelProp="title"
                                        filterOption={(input: any, option: any) => {
                                            let textDigit = removeAcento(input);
                                            let searchableText = removeAcento(option?.searchValue || '');
                                            return searchableText.indexOf(textDigit) >= 0;
                                        }}
                                        options={fontes?.map((e) => ({
                                            value: e.id,
                                            title: `${e.numero} - ${e.descricao}`,
                                            searchValue: `${e.numero} - ${e.descricao} ${e.categoria?.nome || ''}`,
                                            label: (
                                                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, padding: '2px 0' }}>
                                                    <span style={{ fontWeight: 500 }}>{e.numero} - {e.descricao}</span>
                                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                        {e.categoria?.nome}
                                                    </Typography.Text>
                                                </div>
                                            ),
                                        })) || []}
                                        dropdownRender={menu => (
                                            <div>
                                                <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8 }}>
                                                    <Button
                                                        block
                                                        onClick={handleClickAdicionarFonte}
                                                        type="dashed"
                                                    >
                                                        Adicionar Fonte
                                                    </Button>
                                                </div>
                                                <Divider style={{ margin: '4px 0' }} />
                                                {menu}
                                            </div>
                                        )}
                                    />
                                </Form.Item>

                                {isFonteFormVisible && solicitacao && (
                                    <FonteForm
                                        onCancel={handleCloseFonteForm}
                                        solicitacaoId={solicitacao.id}
                                        onFinish={handleFonteCreated}
                                        onCreateFonte={onCreateFonte}
                                    />
                                )}
                            </Form>
                        </div>

                        <Row style={{ marginTop: 24, paddingTop: 12, borderTop: `1px solid ${isLight ? '#f0f0f0' : '#303030'}` }}>
                            <Col span={24} style={{ textAlign: "end" }}>
                                <Button
                                    style={{ marginRight: 10 }}
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </Button>

                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    onClick={handleSubmit}
                                    loading={isSubmitting}
                                    disabled={isSubmitting || selectedContas.length < 2}
                                >
                                    Salvar fontes
                                </Button>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </Modal>
    );
}
